/**
 * What the browser tab says.
 *
 * ## Why the model comes first
 *
 * Reported with a screenshot of three tabs reading
 * `TensorLens - look inside an A…`, all identical, all different models
 * (2026-08-09). Tabs truncate from the right, so anything shared by every page
 * is dead weight at the front: `TensorLens — prism-ml/Terna…` still gives
 * twelve identical characters before the first distinguishing one.
 *
 * Model-first is also the ordinary convention for a titled page, and it is
 * already what a shared link says — `og-metadata.ts` emits
 * `${repoId} — TensorLens`. One phrasing across the tab, the OG card and the
 * search result costs nothing and means a person who saw the card recognises
 * the tab.
 *
 * ## Why the tensor is not in it
 *
 * A tab is read at a glance and truncated hard; a tensor name is long, changes
 * on every click, and is the one thing already visible on screen. The URL
 * carries it for sharing, which is the job it needs to do.
 *
 * ## Why the landing title is passed in rather than declared here
 *
 * It would be a *fifth* copy of a string `main.ts` already counts copies of —
 * `<title>`, `og:title` and `og:image:alt` in `index.html`, plus `HEADLINE` in
 * `og/src/fallback-card.ts`, with a comment recording that they drifted apart
 * once already. The caller captures `document.title` before anything changes
 * it, so the default is whatever the document actually shipped with and cannot
 * disagree with it.
 *
 * Pure: state in, string out. The caller owns `document`.
 */

const SITE_NAME = "TensorLens";

export interface TitleState {
  /** The loaded model, or `undefined` on the landing screen. */
  readonly repoId: string | undefined;
  /** The comparison's other side, when compare mode is on. */
  readonly compareRepoId?: string | undefined;
  /**
   * Which model inside the repo, when it holds several — `vae/`, `unet/`.
   *
   * Included because two tabs open on two components of the *same* repo are
   * otherwise indistinguishable, which is the exact complaint this file exists
   * for, one level down.
   */
  readonly component?: string | undefined;
  /**
   * Which model inside B, for the same reason.
   *
   * Comparing two quantizations of one repo is the tool's sharpest use, and
   * without this the tab read `owner/model/Q4_K_M vs owner/model` — a model
   * apparently compared against itself, which is both wrong and exactly the
   * indistinguishable-tabs complaint this file exists for.
   */
  readonly compareComponent?: string | undefined;
}

export function documentTitle(state: TitleState, siteDefault: string): string {
  if (!state.repoId) return siteDefault;

  const named = (repoId: string, component: string | undefined): string => {
    const part = component?.replace(/\/$/, "");
    return part ? `${repoId}/${part}` : repoId;
  };
  const subject = named(state.repoId, state.component);

  // Compare mode is about the pair, so the pair is the subject. `vs` rather
  // than an arrow: it survives a font that has no glyph for one.
  const left = state.compareRepoId
    ? `${subject} vs ${named(state.compareRepoId, state.compareComponent)}`
    : subject;
  return `${left} — ${SITE_NAME}`;
}
